import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Stack,
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Chip,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Checkbox,
  Snackbar,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import VerifiedIcon from "@mui/icons-material/Verified";
import { getPendingDocuments, bulkVerifyDocuments } from "api/onboardingApi";
import { unwrapList, unwrapObject } from "api/unwrap";

/**
 * /compliance/pending-documents — admin queue of every uploaded driver document
 * that hasn't been verified yet. Multi-select rows and click "Verify selected"
 * to verify in one shot.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

export const PendingDocumentsPage = () => {
  const navigate = useNavigate();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [selected, setSelected] = useState(new Set());
  const [verifying, setVerifying] = useState(false);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getPendingDocuments();
      setDocs(unwrapList(res));
      setSelected(new Set());
    } catch (e) {
      setError(e?.message || "Failed to load pending documents");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const types = useMemo(() => {
    const set = new Set();
    docs.forEach((d) => set.add(d.documentType));
    return Array.from(set).sort();
  }, [docs]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return docs.filter((d) => {
      if (typeFilter !== "all" && d.documentType !== typeFilter) return false;
      if (!q) return true;
      const hay = `${d.driverFullName} ${d.driverEmail || ""} ${d.driverPhone || ""} ${d.documentName} ${d.documentType}`.toLowerCase();
      return hay.includes(q);
    });
  }, [docs, search, typeFilter]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allFilteredSelected = filtered.length > 0 && filtered.every((d) => selected.has(d.id));

  const toggleAll = () => {
    if (allFilteredSelected) {
      setSelected((prev) => {
        const next = new Set(prev);
        filtered.forEach((d) => next.delete(d.id));
        return next;
      });
    } else {
      setSelected((prev) => {
        const next = new Set(prev);
        filtered.forEach((d) => next.add(d.id));
        return next;
      });
    }
  };

  const handleBulkVerify = async () => {
    if (selected.size === 0) return;
    setVerifying(true);
    setError(null);
    try {
      const res = await bulkVerifyDocuments(Array.from(selected));
      const verifiedCount = res?.value?.verifiedCount ?? res?.data?.verifiedCount ?? res?.verifiedCount ?? 0;
      setToast(`Verified ${verifiedCount} document${verifiedCount === 1 ? "" : "s"}`);
      load();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Bulk verify failed");
    } finally {
      setVerifying(false);
    }
  };

  const ageLabel = (ms) => {
    if (!ms) return "—";
    const days = Math.floor((Date.now() - ms) / DAY_MS);
    if (days === 0) return "today";
    if (days === 1) return "1 day ago";
    return `${days} days ago`;
  };

  return (
    <Stack spacing={2} sx={{ p: { xs: 2, md: 3 }, backgroundColor: "#f8fafc", minHeight: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: "#0f172a" }}>
            Pending Documents ({docs.length})
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Every driver-uploaded document still awaiting admin verification. Select rows and click "Verify selected" to clear the queue.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="contained"
            startIcon={verifying ? <CircularProgress size={16} sx={{ color: "white" }} /> : <VerifiedIcon />}
            onClick={handleBulkVerify}
            disabled={selected.size === 0 || verifying}
            sx={{ backgroundColor: "#16a34a", "&:hover": { backgroundColor: "#15803d" } }}
          >
            Verify selected ({selected.size})
          </Button>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
            Refresh
          </Button>
        </Box>
      </Box>

      {error ? <Alert severity="error" onClose={() => setError(null)}>{error}</Alert> : null}

      <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
        <TextField
          size="small"
          placeholder="Search driver / doc name / type"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, minWidth: 240 }}
        />
        <Chip
          size="small"
          label={`All (${docs.length})`}
          onClick={() => setTypeFilter("all")}
          sx={{
            backgroundColor: typeFilter === "all" ? "#1F4E78" : "#f1f5f9",
            color: typeFilter === "all" ? "#ffffff" : "#475569",
            fontWeight: typeFilter === "all" ? 700 : 500,
            cursor: "pointer",
          }}
        />
        {types.map((t) => {
          const count = docs.filter((d) => d.documentType === t).length;
          return (
            <Chip
              key={t}
              size="small"
              label={`${t} (${count})`}
              onClick={() => setTypeFilter(t)}
              sx={{
                backgroundColor: typeFilter === t ? "#1F4E78" : "#f1f5f9",
                color: typeFilter === t ? "#ffffff" : "#475569",
                fontWeight: typeFilter === t ? 700 : 500,
                cursor: "pointer",
              }}
            />
          );
        })}
      </Box>

      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ "& th": { fontWeight: 700, backgroundColor: "#f8fafc" } }}>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  indeterminate={selected.size > 0 && !allFilteredSelected}
                  checked={allFilteredSelected}
                  onChange={toggleAll}
                  disabled={filtered.length === 0}
                />
              </TableCell>
              <TableCell>Driver</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>File</TableCell>
              <TableCell align="right">Size</TableCell>
              <TableCell align="center">Uploaded</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && docs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} sx={{ textAlign: "center", py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
                  {docs.length === 0
                    ? "Queue is clear. 🎉"
                    : "No documents match the current filter."}
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((d) => (
                <TableRow
                  key={d.id}
                  hover
                  selected={selected.has(d.id)}
                  onClick={() => toggle(d.id)}
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={selected.has(d.id)}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => toggle(d.id)}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{d.driverFullName}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {d.driverEmail || d.driverPhone || "—"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={d.documentType} sx={{ height: 20, fontSize: 11 }} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 240 }}>
                      {d.documentName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {d.mimeType || "—"}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="caption" color="text.secondary">
                      {d.fileSizeBytes ? `${Math.round(d.fileSizeBytes / 1024)} KB` : "—"}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="caption" color="text.secondary">
                      {ageLabel(d.uploadedAt)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end" }} onClick={(e) => e.stopPropagation()}>
                      {d.publicUrl ? (
                        <Button
                          size="small"
                          href={d.publicUrl}
                          target="_blank"
                          rel="noreferrer"
                          endIcon={<OpenInNewIcon fontSize="small" />}
                        >
                          View
                        </Button>
                      ) : null}
                      <Button
                        size="small"
                        onClick={() => navigate(`/compliance/drivers/${d.driverDetailsId}`)}
                      >
                        Driver
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        message={toast || ""}
      />
    </Stack>
  );
};

export default PendingDocumentsPage;
